
import React from "react";
import { SupportTicket } from "@/lib/types/support-tickets";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import TicketStatusBadge from "./TicketStatusBadge";
import TicketPriorityBadge from "./TicketPriorityBadge";
import TicketTypeBadge from "./TicketTypeBadge";
import { format } from "date-fns";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/context/auth-context";
import { updateTicket } from "@/lib/services/support-ticket-service";
import { Badge } from "@/components/ui/badge";
import { Clock, User } from "lucide-react";

interface TicketDetailsSidebarProps {
  ticket: SupportTicket;
  onTicketUpdated: (ticket: SupportTicket) => void;
}

const TicketDetailsSidebar: React.FC<TicketDetailsSidebarProps> = ({
  ticket,
  onTicketUpdated
}) => {
  const { profile } = useAuth();
  const isStaff = profile?.role === 'staff' || profile?.role === 'admin' || profile?.role === 'tech';
  
  const [status, setStatus] = React.useState<SupportTicket['status']>(ticket.status);
  const [priority, setPriority] = React.useState<SupportTicket['priority']>(ticket.priority);
  const [isSaving, setIsSaving] = React.useState(false);
  
  // Update state when ticket prop changes
  React.useEffect(() => {
    setStatus(ticket.status);
    setPriority(ticket.priority);
  }, [ticket]);
  
  const handleSaveChanges = async () => {
    if (!isStaff) return;
    
    setIsSaving(true);
    
    const updatedTicket = await updateTicket(ticket.id, {
      status,
      priority,
      // If status is changed to closed, set closed_at
      ...(status === 'closed' && ticket.status !== 'closed' ? { closed_at: new Date().toISOString() } : {}),
      // If status is changed from closed, clear closed_at
      ...(status !== 'closed' && ticket.status === 'closed' ? { closed_at: null } : {})
    });
    
    setIsSaving(false);
    
    if (updatedTicket) {
      onTicketUpdated(updatedTicket);
    }
  };
  
  const createdDate = format(new Date(ticket.created_at), "MMM d, yyyy 'at' h:mm a");
  const updatedDate = format(new Date(ticket.updated_at), "MMM d, yyyy 'at' h:mm a");
  const closedDate = ticket.closed_at ? format(new Date(ticket.closed_at), "MMM d, yyyy 'at' h:mm a") : null;
  
  const hasChanges = status !== ticket.status || priority !== ticket.priority;
  
  // Type-safe handlers for Select components
  const handleStatusChange = (value: string) => {
    setStatus(value as SupportTicket['status']);
  };
  
  const handlePriorityChange = (value: string) => {
    setPriority(value as SupportTicket['priority']);
  };
  
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Ticket Status</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {isStaff ? (
            <>
              <div className="space-y-2">
                <label className="text-sm font-medium">Status</label>
                <Select value={status} onValueChange={handleStatusChange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="pending_info">Pending Info</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Priority</label>
                <Select value={priority} onValueChange={handlePriorityChange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <Button 
                className="w-full"
                onClick={handleSaveChanges}
                disabled={!hasChanges || isSaving}
              >
                {isSaving ? "Saving..." : "Save Changes"}
              </Button>
            </>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Status:</span>
                <TicketStatusBadge status={ticket.status} />
              </div>
              
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Priority:</span>
                <TicketPriorityBadge priority={ticket.priority} />
              </div>
              
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Type:</span>
                <TicketTypeBadge type={ticket.ticket_type} />
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Ticket Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Submitted by:</span>
            <Badge variant="outline">{ticket.creator_name}</Badge>
          </div>
          
          {ticket.department && (
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Department:</span>
              <span className="text-sm">{ticket.department}</span>
            </div>
          )}
          
          {ticket.related_component && (
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Component:</span>
              <span className="text-sm">{ticket.related_component}</span>
            </div>
          )}
          
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Created:</span>
            <span className="text-sm">{createdDate}</span>
          </div>
          
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Updated:</span>
            <span className="text-sm">{updatedDate}</span>
          </div>
          
          {closedDate && (
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Closed:</span>
              <span className="text-sm">{closedDate}</span>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TicketDetailsSidebar;
